import { action, autorun, flow, observable, runInAction } from "mobx";
import { incrementValueVersion } from "./incrementValueVersion";
import {
  addUnsavedChange,
  unsavedChange,
  unsavedChanges,
} from "./unsavedChanges";
import { awaitableWhen, idCounter, wait } from "./util";

export interface GetValueVersionerParams {
  setValue: (val: any, valueIsFromBackend: boolean) => any;
  setValueVersion?: (valueVersion: number) => any;
  getValueVersion?: () => number;
  debounce?: number;
  canDebounceMutations?: boolean;
  getBackendValue?: () => GetVersionedBackendValueResponse;
}

export const getValueVersioner = (p: GetValueVersionerParams) => {
  const {
    canDebounceMutations = true,
    debounce = 1000,
    getValueVersion = () => defaultVersionBox.get(),
    setValueVersion = action((valueVersion) => {
      defaultVersionBox.set(valueVersion);
    }),
    setValue,
    getBackendValue,
  } = p;

  const disposerFunctions: (() => any)[] = [];

  function autorunOnNewBackendValue(
    getBackendValue: () => GetVersionedBackendValueResponse
  ) {
    return autorun(() => {
      const r = getBackendValue();
      if (!r) return;

      const { valueVersion, getVal } = r;

      const isWaitingForUpatedData = updateTasks.has(valueVersionerId);

      // the wait for !isWaitingForUpatedData prevents outdated data from being applied
      // when editing two fields in quick succession
      if (
        (valueVersion ?? -1) >= getValueVersion() &&
        !isWaitingForUpatedData
      ) {
        runInAction(() => {
          setValue(getVal(), true);
        });
      }
    });
  }

  if (getBackendValue) {
    disposerFunctions.push(autorunOnNewBackendValue(getBackendValue));
  }

  const defaultVersionBox = observable.box(0);

  let lastDebounceId = -1;

  const valueVersionerId = `valueVersioner${idCounter()}`;

  let onChange = flow(function* (params: ValueVersionerOnChangeParams) {
    const {
      invalidateQuery,
      mutateBackend,
      newValue,
      concurrentMutationGroup,
    } = params;

    // optimistic update of gui
    setValue(newValue, false);

    setValueVersion(incrementValueVersion());

    let myDebounceId = getValueVersion();
    lastDebounceId = myDebounceId;

    const uc = unsavedChange({
      type: "StandardValueChangeType",
      concurrentGroup: concurrentMutationGroup,
      needsConfirmationBeforeUnload: () => true,
      needsConfirmationBeforeSpaRedirect: () => {
        return false;
      },
    });

    const removeUnsavedChange = addUnsavedChange(uc);
    const removeUpdateTask = addUpdateTask(valueVersionerId);

    if (canDebounceMutations && debounce) {
      yield wait(debounce);
      if (myDebounceId !== lastDebounceId) {
        removeUnsavedChange();
        return;
      }
    }

    yield awaitableWhen(() => {
      const pendingBaseFieldMutations = unsavedChanges
        .get()
        .filter((uc) => uc.type === "StandardValueChangeType");

      // wait until all mutations before this one have same mutation group as this.
      // this allows operations with same group to run concurrently.
      // for example it lets
      // several candidateAPI.removeKnowledgeByTypeFromCandidate run at the same time
      // so long as there is no other operation between them in the queue
      // (this prevents building up a long queue while spamming trash can button)
      let allBeforeMeHaveSameGroup = true;
      for (let i = 0; i < pendingBaseFieldMutations.length; i++) {
        let m = pendingBaseFieldMutations[i];
        if (m.key === uc.key) break;

        if (m.concurrentGroup !== uc.concurrentGroup) {
          allBeforeMeHaveSameGroup = false;
          break;
        }
      }

      return allBeforeMeHaveSameGroup;
    });

    try {
      yield mutateBackend(newValue);
    } finally {
      removeUnsavedChange();
    }

    if (!canDebounceMutations && debounce) {
      yield wait(debounce);
    }

    if (myDebounceId !== lastDebounceId) return;

    const invalidateResult = yield invalidateQuery();

    removeUpdateTask();

    return invalidateResult;
  });

  return {
    onChange,
    onNewValue: autorunOnNewBackendValue,
    dispose: () => {
      disposerFunctions.forEach((f) => f());
    },
  };
};

export interface GetVersionedBackendValueResponse {
  valueVersion: number;
  getVal: () => any;
}

const updateTasks = observable.map<string, { [taskId: string]: true }>();

const addUpdateTask = action((valueVersionerId: string) => {
  if (!updateTasks.has(valueVersionerId)) {
    updateTasks.set(valueVersionerId, {});
  }

  const a = updateTasks.get(valueVersionerId) || {};
  updateTasks.set(valueVersionerId, { ...a, [valueVersionerId]: true });

  return action(() => {
    const a = updateTasks.get(valueVersionerId) || {};
    delete a[valueVersionerId];

    if (Object.keys(a).length > 0) {
      updateTasks.set(valueVersionerId, { ...a });
    } else {
      updateTasks.delete(valueVersionerId);
    }
  });
});

export interface ValueVersionerOnChangeParams {
  newValue: any;
  mutateBackend: (newValue) => Promise<any>;
  invalidateQuery: () => Promise<any>;
  concurrentMutationGroup?: string;
}
